import { sizedImageUrl } from '@akpl/shared';
import { BUSY_SPINNER_CLASS } from '@akpl/ui-mobile';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { IconNote } from './icons';

/**
 * The one scrolling region of a tab screen (Home, Search, Profile, Queue). The shell is a fixed
 * frame, so the heading above stays put and only this area moves: vertical only, no sideways
 * drift, no chaining into the page behind it, momentum scrolling on iOS.
 *
 * `[&>*]:shrink-0`: the screens put this on a flex column, and a flex item shrinks to fit when
 * its siblings add up to more than the box — the Home hero (a fixed-height card) came out as a
 * thin strip. Nothing in the column shrinks; what does not fit scrolls.
 */
export const SCROLL_AREA_CLASS =
  'min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain pb-6 [-webkit-overflow-scrolling:touch] [&>*]:shrink-0';

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

/**
 * Flat cover colors of the Figma frames — the same palette as `packages/ui-tv`
 * (`tokens.ts`), duplicated here so the phone bundle never pulls the TV package (its CSS
 * modules and bundled Inter faces would come with it).
 */
export const COVER_COLORS: readonly string[] = [
  '#5b8cff',
  '#ff7359',
  '#c65cff',
  '#19e3ff',
  '#ff2d87',
  '#7ed957',
  '#3ed6a5',
  '#ffb33b',
];

/** Deterministic flat color for a string (song id, user name…). */
export function colorFor(seed: string, palette: readonly string[] = COVER_COLORS): string {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[h % palette.length] ?? palette[0]!;
}

export function Cover({
  seed,
  imageUrl,
  size = 48,
  radius = 12,
  className,
}: {
  seed: string;
  imageUrl?: string | null;
  size?: number;
  radius?: number;
  className?: string;
}) {
  return (
    <span
      className={cx('grid shrink-0 place-items-center overflow-hidden text-black/45', className)}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: imageUrl ? 'var(--color-surface-3)' : colorFor(seed),
      }}
    >
      {imageUrl ? (
        <img
          // A thumbnail that covers the box at 2x, not the 500 px original.
          src={sizedImageUrl(imageUrl, size, 2) ?? imageUrl}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
        />
      ) : (
        <IconNote size={Math.round(size * 0.42)} />
      )}
    </span>
  );
}

/** Member avatar: photo when there is one, otherwise the initial on a flat color. */
export function Avatar({
  name,
  photo,
  size = 28,
  className,
}: {
  name: string;
  photo?: string | null;
  size?: number;
  className?: string;
}) {
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  return (
    <span
      className={cx(
        'grid shrink-0 place-items-center overflow-hidden rounded-full font-semibold text-black/70',
        className,
      )}
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.45),
        background: photo ? 'var(--color-surface-3)' : colorFor(name),
      }}
      aria-hidden="true"
    >
      {photo ? (
        <img
          // Profile photos are media-Worker URLs too: a 28 px avatar never needs the original.
          src={sizedImageUrl(photo, size, 2) ?? photo}
          alt=""
          width={size}
          height={size}
          className="h-full w-full object-cover"
          loading="lazy"
          decoding="async"
        />
      ) : (
        initial
      )}
    </span>
  );
}

/** Figma M/Badge 46:264 — a flat pill, never a tinted one. */
export function Badge({ children, tone = 'new' }: { children: ReactNode; tone?: 'new' | 'tone' }) {
  return (
    <span
      className={cx(
        'shrink-0 rounded-[7px] px-2 py-0.5 text-[12px] leading-[1.25] font-bold',
        tone === 'new' ? 'bg-cyan text-bg' : 'bg-surface-3 text-muted',
      )}
    >
      {children}
    </span>
  );
}

/**
 * How many sheets are open right now. The toast host subscribes so it can move out of the way
 * of the sheet's actions (MOB-119) — a module-level counter rather than a store field because
 * a sheet is pure view state and `Toasts` is the only reader.
 */
let openSheets = 0;
const sheetListeners = new Set<() => void>();

function setOpenSheets(next: number) {
  openSheets = next;
  for (const listener of sheetListeners) listener();
}

/** True while at least one {@link Sheet} is mounted. */
export function useSheetOpen(): boolean {
  const subscribe = useCallback((onChange: () => void) => {
    sheetListeners.add(onChange);
    return () => {
      sheetListeners.delete(onChange);
    };
  }, []);
  return useSyncExternalStore(
    subscribe,
    () => openSheets > 0,
    () => false,
  );
}

/** How far the grabber has to be dragged down before the sheet takes it as "dismiss" (UX-411). */
const SHEET_DISMISS_PX = 96;

/**
 * How long the backdrop refuses to dismiss after the sheet opens (UX-482).
 *
 * QA 2026-09-15: a single touch tap on a result's **+** opens the sheet; *two* taps left **zero**
 * sheets at 60, 120, 250 **and** 400 ms apart, because the second tap landed on the backdrop the
 * sheet had just slid under the finger. An impatient double tap therefore added nothing and said
 * nothing, and the button read as dead — while the same double tap on «Agregar a la fila» and
 * «Reproducir ahora» was correctly de-duplicated. The guard covers the slide-in and a human
 * double tap; Escape, the close button and the grabber are never blocked, so the sheet is always
 * dismissible by an intentional gesture.
 */
export const SHEET_BACKDROP_ARM_MS = 450;
/** Marks the history entry a sheet pushes, so only its own entry is ever popped (UX-402). */
const SHEET_HISTORY_KEY = '__akmSheet';

let sheetSeq = 0;

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Bottom sheet (Figma M-C2 50:759). Focus is moved into the panel on open and trapped there,
 * the backdrop and Escape close it, and on Android so does the system Back button.
 *
 * **Back (UX-402).** The sheet is pure view state, so with it open one Back used to navigate the
 * companion to Home *underneath the still-open sheet* and a second one left the app entirely —
 * on Android, Back is how people close a bottom sheet, which made the modal inescapable except
 * through the scrim. Opening therefore pushes one history entry and `popstate` closes the sheet;
 * closing any other way pops that entry again, so the stack is exactly as deep as it was.
 *
 * **Focus (UX-401).** `aria-modal` alone stops nothing: the eighth Tab used to land on `BODY`
 * and from there walk the search screen behind the scrim. Tab now cycles inside the panel, and
 * the control that opened the sheet gets the focus back when it closes.
 */
export function Sheet({
  open,
  onClose,
  labelledBy,
  children,
  testId,
}: {
  open: boolean;
  onClose: () => void;
  labelledBy?: string;
  children: ReactNode;
  testId?: string;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  // `onClose` is read from listeners that outlive the render that installed them.
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });
  const [drag, setDrag] = useState(0);
  /** When this sheet opened — the backdrop is inert until {@link SHEET_BACKDROP_ARM_MS} after it. */
  const openedAt = useRef(0);

  // Tell the toast host a sheet owns the bottom of the screen (MOB-119).
  useEffect(() => {
    if (!open) return undefined;
    setOpenSheets(openSheets + 1);
    return () => setOpenSheets(Math.max(0, openSheets - 1));
  }, [open]);

  // UX-402: one history entry per open sheet, popped again when it closes any other way.
  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;
    sheetSeq += 1;
    const id = sheetSeq;
    let popped = false;
    window.history.pushState({ [SHEET_HISTORY_KEY]: id }, '');
    const onPop = () => {
      popped = true;
      close.current();
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // Only ever unwind our own entry: if the app navigated on top of it, leaving the stack
      // alone is far better than sending the user a page back.
      const state = window.history.state as Record<string, unknown> | null;
      if (!popped && state?.[SHEET_HISTORY_KEY] === id) window.history.back();
    };
  }, [open]);

  // UX-401: focus into the panel, trapped while it is open, restored to the opener on close.
  useEffect(() => {
    if (!open) return undefined;
    openedAt.current = Date.now();
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close.current();
        return;
      }
      if (e.key !== 'Tab' || !panel.current) return;
      const stops = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      const first = stops[0] ?? panel.current;
      const last = stops[stops.length - 1] ?? panel.current;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panel.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (active instanceof Node && !panel.current.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const back = opener.current;
      opener.current = null;
      if (back?.isConnected) back.focus();
    };
  }, [open]);

  if (!open) return null;

  /**
   * UX-411: the grabber is drawn and reads as draggable, which on a phone is the first gesture
   * people try — and it did nothing. Past {@link SHEET_DISMISS_PX} the sheet closes; short of
   * it the panel springs back.
   */
  const onGrabberDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const startY = event.clientY;
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    const move = (e: PointerEvent) => setDrag(Math.max(0, e.clientY - startY));
    const up = (e: PointerEvent) => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
      setDrag(0);
      if (e.clientY - startY >= SHEET_DISMISS_PX) close.current();
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
  };

  return (
    // `h-[100dvh]` next to `inset-0`: the sheet must end at the bottom of the *visible* screen,
    // which is what `dvh` tracks while a mobile URL bar collapses (QA 2026-09-14, MOB-71).
    <div className="fixed inset-0 z-50 flex h-[100dvh] max-h-[100dvh] flex-col justify-end">
      <button
        type="button"
        aria-label="close"
        data-testid="sheet-backdrop"
        // UX-482: the second half of a double tap on the opener lands here; it is absorbed.
        onClick={() => {
          if (Date.now() - openedAt.current < SHEET_BACKDROP_ARM_MS) return;
          close.current();
        }}
        className="absolute inset-0 h-full w-full cursor-default border-0 bg-black/60"
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        {...(labelledBy ? { 'aria-labelledby': labelledBy } : {})}
        data-testid={testId}
        tabIndex={-1}
        style={drag > 0 ? { transform: `translateY(${drag}px)` } : undefined}
        className="relative flex max-h-full w-full flex-col gap-4 overflow-y-auto overscroll-contain rounded-t-[24px] bg-surface-2 px-5 pt-2.5 pb-[max(34px,var(--akm-safe-bottom))] outline-none"
      >
        <div
          data-testid="sheet-grabber"
          role="presentation"
          onPointerDown={onGrabberDown}
          className="mx-auto -mt-1 flex h-6 w-16 shrink-0 cursor-grab touch-none items-center justify-center"
        >
          <span className="h-1 w-10 rounded-[2px] bg-surface-3" />
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Inline busy spinner (press system, 2026-10-01): 16 px, `currentColor`, 0.7 — next to a label
 * that stays visible, or in place of the glyph on a round button. Decorative: the words are in
 * {@link BusyStatus}.
 */
export function InlineSpinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      data-testid="busy-spinner"
      className={cx(BUSY_SPINNER_CLASS, className)}
    />
  );
}

/**
 * The polite live region that says what a busy control is doing ("Agregando a la fila…").
 * Always mounted, so the announcement is a text change a screen reader actually reads.
 */
export function BusyStatus({ label }: { label: string | null }) {
  return (
    <span role="status" aria-live="polite" className="sr-only">
      {label ?? ''}
    </span>
  );
}
